import {
    ButtonSecondary,
    Card,
    Colors,
    Icon,
    SearchBar,
    SelectOutlined,
    Text,
    useModalPortalTarget,
    type OptionProps,
} from '@amelio/ui-library';
import {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
    type CSSProperties,
    type RefObject,
} from 'react';
import { createPortal } from 'react-dom';

import type { TranslationKey } from '@/i18n-key';
import type { UserFilterParams } from '@/types/api';
import type { Company } from '@/types/directory/Company';

import {
    useCollectCompanyPeopleIds,
    useGetCompanyInformation,
    useGetCompanyPeople,
    useGetUserPermissions,
} from '@/api/directory';
import { useAmelioTranslations } from '@/hooks';
import { getCompanyAttributeList } from '@/utils/company';
import { getLocalizedText } from '@/utils/localization';

const PREVIEW_PAGE_SIZE = 50;

// Local mirror of ui-library's unexported useAnchoredDropdownStyle: position:fixed menu anchored to the field, above the modal, tracking scroll/resize.
const DROPDOWN_GAP = 4;
const DROPDOWN_Z_INDEX = 9999;

const useAnchoredDropdownStyle = (
    anchorRef: RefObject<HTMLElement | null>,
    isOpen: boolean,
): CSSProperties => {
    const [style, setStyle] = useState<CSSProperties>({});

    useLayoutEffect(() => {
        if (!isOpen) {
            return;
        }

        const updatePosition = () => {
            const anchor = anchorRef.current;
            if (!anchor) {
                return;
            }

            const rect = anchor.getBoundingClientRect();
            setStyle({
                position: 'fixed',
                width: rect.width,
                top: rect.bottom + DROPDOWN_GAP,
                left: rect.left,
                zIndex: DROPDOWN_Z_INDEX,
                pointerEvents: 'auto',
            });
        };

        updatePosition();
        window.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition);

        const resizeObserver = new ResizeObserver(updatePosition);

        if (anchorRef.current) {
            resizeObserver.observe(anchorRef.current);
        }

        return () => {
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
            resizeObserver.disconnect();
        };
    }, [anchorRef, isOpen]);

    return style;
};

// Standard attribute families offered as rule cards, in the same order as getCompanyAttributeList.
const STANDARD_ATTRIBUTE_KEYS = [
    'departments',
    'jobTitles',
    'companyAddresses',
    'employeeLevels',
    'ageGroups',
    'tenures',
    'managers',
] as const;

// Standard family bucket key -> its UserFilterParams field; custom families funnel into customAttributeIds.
const STANDARD_ATTRIBUTE_TO_FILTER_KEY: Record<string, keyof UserFilterParams> = {
    jobTitles: 'jobTitleIds',
    departments: 'departmentIds',
    companyAddresses: 'companyAddressIds',
    employeeLevels: 'employeeLevelIds',
    managers: 'managerIds',
};

// OR within a card's values, AND across cards.
interface RuleCard {
    id: string;
    attribute: string;
    valueIds: string[];
    search: string;
}

const createEmptyRuleCard = (): RuleCard => ({
    id: crypto.randomUUID(),
    attribute: '',
    valueIds: [],
    search: '',
});

// A card only targets employees once it carries an attribute AND at least one of its values.
const getIncompleteRuleKey = (card: RuleCard): TranslationKey | undefined => {
    if (!card.attribute) {
        return 'competencies.assignToEmployees.rule.attributeRequired';
    }

    if (card.valueIds.length < 1) {
        return 'competencies.assignToEmployees.rule.valuesRequired';
    }

    return undefined;
};

const getAttributeFamilies = (company?: Company): OptionProps[] => {
    const attributes = getCompanyAttributeList(company);

    const standardFamilies = STANDARD_ATTRIBUTE_KEYS.filter((key) => attributes[key]).map(
        (key) => ({
            value: key,
            label: attributes[key].name,
        }),
    );

    const customFamilies = Object.entries(attributes)
        .filter(([key]) => !STANDARD_ATTRIBUTE_KEYS.includes(key as never))
        .map(([key, attribute]) => ({ value: key, label: attribute.name }));

    return [...standardFamilies, ...customFamilies];
};

const getFilterKeyForAttribute = (attribute: string): keyof UserFilterParams =>
    STANDARD_ATTRIBUTE_TO_FILTER_KEY[attribute] ?? 'customAttributeIds';

export interface RuleSelectionSummaryItem {
    attribute: string;
    values: string[];
}

export interface EmployeeRuleSelectorHandle {
    resolveEmployeeIds: () => Promise<string[]>;
    getSelectionSummary: () => RuleSelectionSummaryItem[];
    matchedCount: number;
    isReady: boolean;
}

export interface RuleSelectorPersistedState {
    ruleCards: RuleCard[];
}

interface EmployeeRuleSelectorProps {
    onMatchedCountChange?: (count: number) => void;
    initialState?: RuleSelectorPersistedState;
    onStateChange?: (state: RuleSelectorPersistedState) => void;
    // When true, visually highlight the attribute select when no selection is present.
    highlightEmpty?: boolean;
}

const buildFilterParams = (
    ruleCards: RuleCard[],
    valueOptionsByAttribute: Record<string, OptionProps[]>,
): UserFilterParams => {
    const params: UserFilterParams = {};

    ruleCards.forEach((card) => {
        if (!card.attribute || card.valueIds.length < 1) {
            return;
        }

        // Guard against ids that no longer exist among the current attribute values.
        const availableIds = new Set(
            (valueOptionsByAttribute[card.attribute] ?? []).map((option) => String(option.value)),
        );
        const validIds = card.valueIds.filter((valueId) => availableIds.has(valueId));

        if (validIds.length < 1) {
            return;
        }

        // Custom families share one customAttributeIds bucket, so merge rather than overwrite.
        const filterKey = getFilterKeyForAttribute(card.attribute);
        params[filterKey] = [...(params[filterKey] ?? []), ...validIds];
    });

    return params;
};

export const EmployeeRuleSelector = forwardRef<
    EmployeeRuleSelectorHandle,
    EmployeeRuleSelectorProps
>(({ onMatchedCountChange, initialState, onStateChange, highlightEmpty }, ref) => {
    const { t, i18n } = useAmelioTranslations();

    const { data: company } = useGetCompanyInformation();
    const { data: permissions, isLoading: permissionsLoading } = useGetUserPermissions();

    const [ruleCards, setRuleCards] = useState<RuleCard[]>(
        () => initialState?.ruleCards ?? [createEmptyRuleCard()],
    );

    const [openCardId, setOpenCardId] = useState<string | null>(null);

    const valueFieldRef = useRef<HTMLDivElement>(null);
    const dropdownStyle = useAnchoredDropdownStyle(valueFieldRef, openCardId !== null);
    // Portal into the styled Shadow DOM so the menu keeps its Tailwind styling.
    const portalTarget = useModalPortalTarget();

    useEffect(() => {
        onStateChange?.({ ruleCards });
    }, [ruleCards, onStateChange]);

    const ruleAttributeOptions = useMemo<OptionProps[]>(
        () => getAttributeFamilies(company),
        [company, i18n.language],
    );

    const ruleValueOptionsByAttribute = useMemo(() => {
        const attributes = getCompanyAttributeList(company);
        const optionsByAttribute: Record<string, OptionProps[]> = {};

        ruleAttributeOptions.forEach((family) => {
            const key = String(family.value);
            optionsByAttribute[key] = (attributes[key]?.values ?? [])
                .filter((value): value is typeof value & { id: string } => Boolean(value.id))
                .map((value) => ({
                    value: value.id,
                    // Custom values carry localized `names`; standard families and managers carry a plain `name`.
                    label: 'names' in value ? getLocalizedText(value.names) : value.name,
                }));
        });

        return optionsByAttribute;
    }, [company, i18n.language, ruleAttributeOptions]);

    const usedRuleAttributes = useMemo(
        () => new Set(ruleCards.map((card) => card.attribute).filter(Boolean)),
        [ruleCards],
    );

    const updateRuleCard = (cardId: string, changes: Partial<RuleCard>) => {
        setRuleCards((cards) =>
            cards.map((card) => (card.id === cardId ? { ...card, ...changes } : card)),
        );
    };

    const removeRuleCard = (cardId: string) => {
        setRuleCards((cards) => cards.filter((card) => card.id !== cardId));
    };

    const addRuleCard = () => {
        setRuleCards((cards) => [...cards, createEmptyRuleCard()]);
    };

    const filterParams = useMemo<UserFilterParams>(
        () => buildFilterParams(ruleCards, ruleValueOptionsByAttribute),
        [ruleCards, ruleValueOptionsByAttribute],
    );

    const collectEmployeeIds = useCollectCompanyPeopleIds({
        canViewResultsOfAllDepartments: permissions?.canViewResultsOfAllDepartments,
    });

    // Single-page query only used to display the live preview count (totalCount).
    const { data: previewData } = useGetCompanyPeople(
        {
            pageNumber: 1,
            pageSize: PREVIEW_PAGE_SIZE,
            isActiveOnly: true,
            includeInvited: true,
            ...filterParams,
        },
        {
            canViewResultsOfAllDepartments: permissions?.canViewResultsOfAllDepartments,
        },
    );

    const matchedCount = previewData?.totalCount ?? 0;
    const isReady =
        !permissionsLoading && permissions?.canViewResultsOfAllDepartments !== undefined;

    const getSelectionSummary = useCallback((): RuleSelectionSummaryItem[] => {
        return ruleCards
            .filter((card) => card.attribute && card.valueIds.length > 0)
            .map((card) => {
                const attributeLabel = String(
                    ruleAttributeOptions.find((option) => option.value === card.attribute)?.label ??
                        card.attribute,
                );
                const valueOptions = ruleValueOptionsByAttribute[card.attribute] ?? [];
                const values = card.valueIds.map((valueId) =>
                    String(
                        valueOptions.find((option) => String(option.value) === valueId)?.label ??
                            valueId,
                    ),
                );

                return { attribute: attributeLabel, values };
            });
    }, [ruleCards, ruleAttributeOptions, ruleValueOptionsByAttribute]);

    useImperativeHandle(
        ref,
        () => ({
            resolveEmployeeIds: () => collectEmployeeIds(filterParams),
            getSelectionSummary,
            matchedCount,
            isReady,
        }),
        [collectEmployeeIds, filterParams, getSelectionSummary, matchedCount, isReady],
    );

    useEffect(() => {
        onMatchedCountChange?.(matchedCount);
    }, [matchedCount, onMatchedCountChange]);

    return (
        <>
            <Text
                color={Colors.BASE_400}
                className={'mt-2'}
            >
                {t('competencies.assignToEmployees.rule.subtitle')}
            </Text>

            <div className={'mt-4 flex flex-col gap-4'}>
                {ruleCards.map((card, index) => {
                    const cardAttributeOptions = ruleAttributeOptions.filter(
                        (option) =>
                            option.value === card.attribute ||
                            !usedRuleAttributes.has(String(option.value)),
                    );

                    const valueOptions = card.attribute
                        ? (ruleValueOptionsByAttribute[card.attribute] ?? [])
                        : [];

                    const selectedValueOptions = valueOptions.filter((option) =>
                        card.valueIds.includes(String(option.value)),
                    );

                    const incompleteRuleKey = highlightEmpty
                        ? getIncompleteRuleKey(card)
                        : undefined;

                    const search = card.search.trim().toLowerCase();
                    const filteredValueOptions = valueOptions.filter(
                        (option) =>
                            !card.valueIds.includes(String(option.value)) &&
                            (search.length < 1 ||
                                String(option.label).toLowerCase().includes(search)),
                    );

                    return (
                        <div
                            key={card.id}
                            className={"relative z-0 has-[button[aria-expanded='true']]:z-50"}
                            data-testid={`rule-card-${index}`}
                        >
                            <Card className={'p-4'}>
                                <div className={'flex items-center justify-between'}>
                                    <div className={'flex items-center gap-2'}>
                                        <span
                                            className={
                                                'bg-secondary-100 text-secondary-700 flex h-6 w-6 items-center justify-center rounded-full text-sm font-semibold'
                                            }
                                        >
                                            {index + 1}
                                        </span>

                                        <Text className={'font-semibold'}>
                                            {t(
                                                'competencies.assignToEmployees.rule.attributeLabel',
                                            )}
                                        </Text>
                                    </div>

                                    {ruleCards.length > 1 && (
                                        <button
                                            type={'button'}
                                            onClick={() => removeRuleCard(card.id)}
                                            aria-label={t('common.delete')}
                                            data-testid={`rule-card-remove-${index}`}
                                        >
                                            <Icon
                                                name={'essential-trash'}
                                                color={Colors.ERROR}
                                            />
                                        </button>
                                    )}
                                </div>

                                <div
                                    className={'mt-3'}
                                    data-testid={`rule-card-attribute-${index}`}
                                >
                                    <SelectOutlined
                                        options={cardAttributeOptions}
                                        value={card.attribute}
                                        sort={'none'}
                                        error={Boolean(incompleteRuleKey)}
                                        errorMessage={incompleteRuleKey && t(incompleteRuleKey)}
                                        onChange={(nextValue) =>
                                            updateRuleCard(card.id, {
                                                attribute: String(nextValue),
                                                valueIds: [],
                                                search: '',
                                            })
                                        }
                                        placeholder={t(
                                            'competencies.assignToEmployees.rule.attributePlaceholder',
                                        )}
                                    />
                                </div>

                                {card.attribute && (
                                    <div className={'mt-3'}>
                                        <Text className={'mb-2 font-semibold'}>
                                            {t('competencies.assignToEmployees.rule.valuesLabel')}
                                        </Text>

                                        {selectedValueOptions.length > 0 && (
                                            <div className={'mb-2 flex flex-wrap gap-2'}>
                                                {selectedValueOptions.map((option) => (
                                                    <span
                                                        key={String(option.value)}
                                                        className={
                                                            'bg-secondary-100 text-secondary-700 flex items-center gap-1 rounded-full px-3 py-1 text-sm'
                                                        }
                                                        data-testid={`rule-card-value-chip-${index}`}
                                                    >
                                                        {option.label}

                                                        <button
                                                            type={'button'}
                                                            onClick={() =>
                                                                updateRuleCard(card.id, {
                                                                    valueIds: card.valueIds.filter(
                                                                        (valueId) =>
                                                                            valueId !==
                                                                            String(option.value),
                                                                    ),
                                                                })
                                                            }
                                                            aria-label={t('common.delete')}
                                                            data-testid={`rule-card-value-remove-${index}`}
                                                        >
                                                            <Icon
                                                                name={'essential-close'}
                                                                color={Colors.SECONDARY_700}
                                                                size={12}
                                                            />
                                                        </button>
                                                    </span>
                                                ))}
                                            </div>
                                        )}

                                        <div
                                            ref={openCardId === card.id ? valueFieldRef : undefined}
                                            onFocus={() => setOpenCardId(card.id)}
                                            onBlur={(event) => {
                                                if (
                                                    !event.currentTarget.contains(
                                                        event.relatedTarget as Node | null,
                                                    )
                                                ) {
                                                    setOpenCardId((current) =>
                                                        current === card.id ? null : current,
                                                    );
                                                }
                                            }}
                                        >
                                            <SearchBar
                                                value={card.search}
                                                onChange={(nextValue: string) =>
                                                    updateRuleCard(card.id, {
                                                        search: nextValue,
                                                    })
                                                }
                                                placeholder={t(
                                                    'competencies.assignToEmployees.rule.valuesPlaceholder',
                                                )}
                                            />

                                            {openCardId === card.id &&
                                                createPortal(
                                                    <div
                                                        // mouseDown selection keeps focus, so the menu never triggers the field's blur.
                                                        style={dropdownStyle}
                                                        className={
                                                            'border-base-200 scrollbar-glass flex max-h-48 flex-col overflow-y-auto rounded-lg border bg-white py-1 shadow-lg'
                                                        }
                                                        data-testid={`rule-card-value-menu-${index}`}
                                                    >
                                                        {filteredValueOptions.length < 1 ? (
                                                            <Text
                                                                color={Colors.BASE_400}
                                                                className={'px-3 py-2 text-sm'}
                                                            >
                                                                {t(
                                                                    'competencies.assignToEmployees.rule.noValues',
                                                                )}
                                                            </Text>
                                                        ) : (
                                                            filteredValueOptions.map((option) => (
                                                                <button
                                                                    key={String(option.value)}
                                                                    type={'button'}
                                                                    className={
                                                                        'hover:bg-base-100 block w-full border-none bg-transparent px-3 py-2 text-left text-sm'
                                                                    }
                                                                    onMouseDown={(mouseEvent) => {
                                                                        mouseEvent.preventDefault();
                                                                        updateRuleCard(card.id, {
                                                                            valueIds: [
                                                                                ...card.valueIds,
                                                                                String(
                                                                                    option.value,
                                                                                ),
                                                                            ],
                                                                            search: '',
                                                                        });
                                                                    }}
                                                                    data-testid={`rule-card-value-option-${index}`}
                                                                >
                                                                    {option.label}
                                                                </button>
                                                            ))
                                                        )}
                                                    </div>,
                                                    portalTarget ?? document.body,
                                                )}
                                        </div>
                                    </div>
                                )}
                            </Card>
                        </div>
                    );
                })}
            </div>

            <div className={'mt-4'}>
                <ButtonSecondary
                    onClick={addRuleCard}
                    disabled={usedRuleAttributes.size >= ruleAttributeOptions.length}
                    dataTestId={'rule-card-add'}
                >
                    <Icon
                        name={'essential-add'}
                        color={Colors.SECONDARY_700}
                    />

                    {t('competencies.assignToEmployees.rule.addAttribute')}
                </ButtonSecondary>
            </div>

            <div
                className={'bg-base-50 mt-4 rounded-xl p-4'}
                data-testid={'rule-matched-count'}
            >
                <Text className={'font-semibold'}>
                    {t('competencies.assignToEmployees.rule.matchedCount', { count: matchedCount })}
                </Text>
            </div>
        </>
    );
});

EmployeeRuleSelector.displayName = 'EmployeeRuleSelector';
